<div class="jt-wrapper">
  <!-- Unified toolbar: title/stat-segments (left) + filters/export/stats/search (right).
       Sticky so it stays reachable while scrolling a long, dense row list. -->
  <header class="jt-toolbar">
    <div class="jt-toolbar-left">
      <h3 class="jt-title">{{ t.APPLICATIONS_HEADER }}</h3>

      <div class="jt-segments" role="tablist">
        <button
          type="button"
          class="jt-segment"
          role="tab"
          [attr.aria-selected]="statusFilter === 'all'"
          [class.is-active]="statusFilter === 'all'"
          (click)="setStatusFilter('all')"
        >
          <span class="jt-dot jt-dot-neutral"></span>
          <span class="jt-segment-count">{{ totalCount }}</span>
          {{ t.TOTAL_LABEL }}
        </button>

        <button
          type="button"
          class="jt-segment"
          role="tab"
          [attr.aria-selected]="statusFilter === 'pending'"
          [class.is-active]="statusFilter === 'pending'"
          (click)="setStatusFilter('pending')"
        >
          <span class="jt-dot jt-dot-pending"></span>
          <span class="jt-segment-count">{{ pendingCount }}</span>
          {{ t.PENDING_LABEL }}
        </button>

        <button
          type="button"
          class="jt-segment"
          role="tab"
          [attr.aria-selected]="statusFilter === 'inProgress'"
          [class.is-active]="statusFilter === 'inProgress'"
          (click)="setStatusFilter('inProgress')"
        >
          <span class="jt-dot jt-dot-progress"></span>
          <span class="jt-segment-count">{{ inProgressCount }}</span>
          {{ t.IN_PROGRESS_LABEL }}
        </button>

        <button
          type="button"
          class="jt-segment"
          role="tab"
          [attr.aria-selected]="statusFilter === 'rejected'"
          [class.is-active]="statusFilter === 'rejected'"
          (click)="setStatusFilter('rejected')"
        >
          <span class="jt-dot jt-dot-rejected"></span>
          <span class="jt-segment-count">{{ rejectedCount }}</span>
          {{ t.REJECTED_LABEL }}
        </button>
      </div>
    </div>

    <div class="jt-toolbar-right">
      <label class="jt-search">
        <i class="bi bi-search"></i>
        <input
          type="text"
          [placeholder]="t.SEARCH_PLACEHOLDER"
          [ngModel]="searchQuery"
          (ngModelChange)="onSearchInputChange($event)"
        />
      </label>

      <div class="jt-actions">
        <button type="button" class="jt-action-btn" (click)="onToggleFilters()">
          <i class="bi bi-funnel"></i>
          <span class="jt-action-label">{{ t.FILTERS_LABEL }}</span>
          @if (hasActiveFilters) {
            <span class="jt-filter-dot" aria-hidden="true"></span>
          }
        </button>

        <button type="button" class="jt-action-btn" (click)="onExportExcel()">
          <i class="bi bi-download"></i>
          <span class="jt-action-label">{{ t.EXPORT_EXCEL_LABEL }}</span>
        </button>

        <!-- Table vs Card view toggle - both render the exact same
             ApplicationSummaryCardComponent content (see that component's
             shared ng-templates), just arranged differently, so nothing
             about status/match/salary/location/timing/actions can drift
             between the two. -->
        <div class="jt-view-toggle" role="group" [attr.aria-label]="t.VIEW_TOGGLE_ARIA">
          <button
            type="button"
            class="jt-view-toggle-btn"
            [class.is-active]="viewMode === 'table'"
            [attr.aria-pressed]="viewMode === 'table'"
            [pTooltip]="t.VIEW_TABLE_LABEL"
            pTooltipShowDelay="500"
            (click)="setViewMode('table')"
          >
            <i class="bi bi-table"></i>
          </button>
          <button
            type="button"
            class="jt-view-toggle-btn"
            [class.is-active]="viewMode === 'card'"
            [attr.aria-pressed]="viewMode === 'card'"
            [pTooltip]="t.VIEW_CARDS_LABEL"
            pTooltipShowDelay="500"
            (click)="setViewMode('card')"
          >
            <i class="bi bi-grid-3x3-gap"></i>
          </button>
        </div>
      </div>
    </div>
  </header>

  @if (!hasInitialData) {
    <div class="jt-state jt-state-loading jt-state-standalone">
      <div class="jt-spinner" role="status">
        <span class="visually-hidden">{{ t.LOADING }}</span>
      </div>
    </div>
  }

  @if (hasInitialData && filteredApplications.length === 0) {
    <div class="jt-state jt-state-empty jt-state-standalone">
      {{ t.NO_APPLICATIONS_FOUND_MATCHING_FILTERS }}
    </div>
  }

  @if (hasInitialData && filteredApplications.length > 0) {
    @if (viewMode === 'table') {
      <div class="jt-table">
        <!-- Column headers - rendered once; each data row below shares the
             same --jt-row-grid template (defined on .jt-wrapper) so cells
             line up. -->
        <div class="jt-row jt-row-head" role="row">
          <span class="jt-col" role="columnheader">{{ t.COLUMN_POSITION }}</span>
          <span class="jt-col" role="columnheader">{{ t.COLUMN_STATUS }}</span>
          <span class="jt-col" role="columnheader">{{ t.COLUMN_MATCH }}</span>
          <span class="jt-col" role="columnheader">{{ t.COLUMN_SALARY }}</span>
          <span class="jt-col" role="columnheader">{{ t.COLUMN_LOCATION }}</span>
          <span class="jt-col" role="columnheader">{{ t.COLUMN_TIMING }}</span>
          <span class="jt-col jt-col-actions" role="columnheader">{{ t.COLUMN_ACTIONS }}</span>
        </div>

        <div class="jt-rows">
          @for (application of filteredApplications; track application.id) {
            <app-application-summary-card
              viewMode="table"
              [application]="application"
              [refreshTrigger]="refreshTrigger"
            ></app-application-summary-card>
          }
        </div>
      </div>
    } @else {
      <div class="jt-cards-grid">
        @for (application of filteredApplications; track application.id) {
          <app-application-summary-card
            viewMode="card"
            [application]="application"
            [refreshTrigger]="refreshTrigger"
          ></app-application-summary-card>
        }
      </div>
    }

    <footer class="jt-paginator">
      <span class="jt-paginator-summary">{{ paginationSummaryText() }}</span>

      <div class="jt-paginator-controls">
        <label class="jt-page-size">
          <select
            [ngModel]="pageSize"
            (ngModelChange)="changePageSize($event)"
            [attr.aria-label]="t.PAGINATION_PAGE_SIZE_ARIA"
          >
            @for (size of pageSizeOptions; track size) {
              <option [value]="size">{{ size }}</option>
            }
          </select>
        </label>

        <button
          type="button"
          class="jt-action-btn"
          [disabled]="!canGoPrev"
          [attr.aria-label]="t.PAGINATION_PREV_ARIA"
          (click)="goToPrevPage()"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          type="button"
          class="jt-action-btn"
          [disabled]="!canGoNext"
          [attr.aria-label]="t.PAGINATION_NEXT_ARIA"
          (click)="goToNextPage()"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </footer>
  }
</div>
